/* eslint-disable complexity */
import { Intent, StoreSubscription } from '@futrli-sdk/billing';
import { UserProperties } from '@futrli-sdk/users';
import { AnyError, ERROR_STATUS, getErrorStatus, withErrorBoundary, WithErrorBoundaryProps } from '@futrli/errors';
import { injectIntl, WrappedComponentProps } from '@futrli/localisation';
import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import {
  getIntegrationsOnboardingUrl,
  getParamFromRoute,
  getValueFromQuery,
  ProductSlug,
  PREDICT_PRODUCT_SLUG,
  PREDICT_PRODUCT_ID,
  QUERY_PARAMS,
  ROUTES,
  ROUTE_PARAMS,
  workspaceByIdSelector
} from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';
import { findProperty, getUserProperties, updateUserProperty } from '@futrli/users';
import React from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams, useNavigate, Location } from 'react-router-dom';
import { Callback, PaymentFormData, PaymentFormInfo } from '../../billing-forms/types';
import {
  createCustomer,
  createPaymentIntent,
  createSetupIntent,
  createSubscription,
  fetchEstimateWithCoupons,
  fetchOrganisations,
  fetchStoredSubscriptionData,
  fetchSubscription,
  handlePaymentIntent,
  handleSetupIntent
} from '../../core/api';
import { trackEvent, trackPaymentError, trackPaymentSuccess } from '../../core/tracker';
import { getCreateCustomerPayload } from '../../utils/api-formatters';
import { getFormError } from '../../utils/error-utils';
import { getPaymentInfoFromStoredDBData, hasExistingCharges } from '../../utils/utils';
import { PaymentDetailsWR } from '../components/payment-details';
import { StoreState } from '../../core/types';
import { INTEGRATION_LEVEL, PREDICT_BILLING_PRODUCT, TRACKING_EVENTS } from '../../core/constants';

interface State {
  isSubmitting: boolean;
  isLoadingData: boolean;
  isSuccessPayment: boolean;
  organisationId?: string;
  paymentInfo: PaymentFormInfo | null;
  error: JSX.Element | null;
  isEmptyProductSlug: boolean;
}

interface StateToProps {
  readonly workspace: Workspace | WorkspacesListingItem | null;
}

interface RouterProps {
  location: Location;
  params: Record<string, string | undefined>;
  navigate: (path: string) => void;
}

type Props = RouterProps & WithErrorBoundaryProps & WrappedComponentProps & StateToProps;

export class PaymentDetailsContainer extends React.PureComponent<Props, State> {
  public state: Readonly<State> = {
    isSubmitting: false,
    isLoadingData: true,
    isSuccessPayment: false,
    paymentInfo: null,
    error: null,
    isEmptyProductSlug: false
  };

  public componentDidMount() {
    this.loadData();
  }

  public render(): JSX.Element {
    const { paymentInfo, error, isSubmitting, isLoadingData, isSuccessPayment } = this.state;

    if (isLoadingData) {
      return <UILoader isFull={true} />;
    }

    return (
      <PaymentDetailsWR
        isLoading={isSubmitting}
        onStripeError={this.handleOnStripeError}
        isSuccessPayment={isSuccessPayment}
        paymentInfo={paymentInfo}
        error={error}
        onSuccessClick={this.handleOnSuccessClick}
        onSubmit={this.handleOnSubmit}
        onPreSubmit={this.handleOnPreSubmit}
        setIsSubmitting={this.setIsSubmitting}
      />
    );
  }

  private setIsSubmitting = (isSubmitting: boolean) => {
    this.setState(prevState => ({ ...prevState, isSubmitting }));
  };
  /**
   * Handles the pre-submit method. Before submitting, the customer in ChargeBee needs
   * to be created and depending on the existing charges will execute the payment intent
   * flow or the setuo intent one.
   * @param {PaymentFormData} formData
   */
  private handleOnPreSubmit = async (formData?: PaymentFormData) => {
    const { paymentInfo } = this.state;
    const workspaceId = getParamFromRoute(this.props.params, ROUTE_PARAMS.WORKSPACE_ID) as string;

    this.setState({ isSubmitting: true });

    if (!formData) {
      return;
    }

    try {
      await createCustomer(workspaceId, getCreateCustomerPayload(formData));

      return hasExistingCharges(paymentInfo)
        ? this.managePaymentIntentActions(workspaceId)
        : this.manageSetupIntentActions();
    } catch (error) {
      this.handleErrorResponse(error);
    }
  };

  private managePaymentIntentActions(workspaceId: string) {
    const { paymentInfo } = this.state;

    if (!paymentInfo) {
      return;
    }

    return {
      createIntent: async (paymentMethodId?: string): Promise<Intent> => {
        const productEstimate = await fetchEstimateWithCoupons(
          workspaceId,
          (paymentInfo?.productSlug as ProductSlug) || PREDICT_PRODUCT_SLUG,
          paymentInfo?.integrationLevel || INTEGRATION_LEVEL,
          {
            term: paymentInfo.subscription.term,
            coupon_ids: paymentInfo.subscription.coupons,
            quantity: paymentInfo.subscription.addons,
            existing_customer: true,
            source: 'sales-portal',
            plan_type: paymentInfo.subscription.plan_type,
            currency_code: paymentInfo.subscription.currencyCode
          }
        );

        return this.withManageErrors(() =>
          createPaymentIntent(workspaceId, {
            amount: productEstimate.estimated_cost,
            currency_code: productEstimate.currency_code,
            payment_method_id: paymentMethodId
          })
        );
      },
      handleIntent: (intentId: string): Promise<Intent> => {
        return this.withManageErrors(() => handlePaymentIntent(workspaceId, intentId));
      }
    };
  }

  private manageSetupIntentActions() {
    const workspaceId = getParamFromRoute(this.props.params, ROUTE_PARAMS.WORKSPACE_ID) as string;

    return {
      createIntent: (): Promise<Intent> => {
        return this.withManageErrors(() => createSetupIntent(workspaceId));
      },
      handleIntent: (intentId: string): Promise<Intent> => {
        return this.withManageErrors(() => handleSetupIntent(workspaceId, intentId));
      }
    };
  }

  private async withManageErrors(asyncAction: Callback) {
    try {
      return await asyncAction();
    } catch (error) {
      this.handleErrorResponse(error);
    }
  }

  private handleOnSubmit = async (formData: PaymentFormData): Promise<void> => {
    try {
      const { intl } = this.props;
      const { paymentInfo, isEmptyProductSlug } = this.state;
      const { workspace } = this.props;
      const workspaceId = getParamFromRoute(this.props.params, ROUTE_PARAMS.WORKSPACE_ID) as string;

      if (!paymentInfo) {
        return;
      }

      const updatedSubscription = await createSubscription(workspaceId, {
        product_slug: isEmptyProductSlug && workspace && workspace.subscription_state ? '' : paymentInfo.productSlug,
        integration_level: paymentInfo.integrationLevel,
        term: paymentInfo.subscription.term,
        intent_id: formData.paymentMethodId,
        coupon_ids: paymentInfo.subscription.coupons,
        quantity: paymentInfo.subscription.addons,
        start_date: paymentInfo.subscription.creationDate,
        currency_code: paymentInfo.subscription.currencyCode,
        plan_type: paymentInfo.subscription.plan_type
      });

      if (paymentInfo.productSlug === PREDICT_PRODUCT_SLUG) {
        trackPaymentSuccess(PREDICT_BILLING_PRODUCT, intl, updatedSubscription, paymentInfo.totalCharges);
      }

      trackEvent(TRACKING_EVENTS.PURCHASED_SUBSCRIPTION_DIRECT_SALES_PORTAL);

      // We need to set to false the redirect property once the form is filled in.
      await this.setRedirectToPaymentPage(false);

      this.setState({
        isSubmitting: false,
        isSuccessPayment: true,
        isEmptyProductSlug: false
      });
    } catch (error) {
      this.handleErrorResponse(error);
    }
  };

  private handleErrorResponse = (error: AnyError) => {
    const { paymentInfo } = this.state;

    this.setState(
      {
        isSubmitting: false,
        isSuccessPayment: false
      },
      () => {
        if (getErrorStatus(error) !== ERROR_STATUS._400) {
          this.props.onManageError(error);
          return;
        }

        if (paymentInfo?.productSlug === PREDICT_PRODUCT_SLUG) {
          trackPaymentError(PREDICT_BILLING_PRODUCT.name, error, !hasExistingCharges(paymentInfo));
        }

        // @TODO Tech debt - We need to have the errors coming from BE in the same format and with a field id or slug,
        // if we wanna show them properly under each field.
        // This error prop will be removed at that moment and will be used formErrors properly.
        this.setState({
          error: getFormError(error)
        });
      }
    );
  };

  private handleOnStripeError = () => {
    this.setState({ isSubmitting: false });
  };

  /**
   * Loads the data needed to render the form. If the user already has a subscription,
   * then this step is skipped and user is redirected to the root path.
   */
  private loadData = async (): Promise<void> => {
    const { params, location, navigate } = this.props;

    try {
      const workspaceId = getParamFromRoute(params, ROUTE_PARAMS.WORKSPACE_ID) as string;
      const subscription = await fetchSubscription(workspaceId);

      // If user has a subscription, then skip the form and reset the user props
      if (subscription) {
        await this.resetSkipIntegrationsOnboardingStep();
        await this.setRedirectToPaymentPage(false);
        return navigate(ROUTES.ROOT_ROUTE);
      }

      const paymentInfo = await this.getPaymentFormInfo(workspaceId);
      let organisationId = getValueFromQuery(location, QUERY_PARAMS.ORG_ID);

      if (!organisationId) {
        const organisations = await fetchOrganisations(workspaceId);

        organisationId = organisations[0]?.id;
      }

      this.setState({
        organisationId,
        paymentInfo: paymentInfo || null,
        isLoadingData: false
      });
    } catch (error) {
      this.setState(
        {
          paymentInfo: null,
          isLoadingData: false
        },
        () => {
          this.props.onManageError(error);
        }
      );
    }
  };

  private getPaymentFormInfo = async (workspaceId: string): Promise<PaymentFormInfo> => {
    const storedSubscriptionData: StoreSubscription = await fetchStoredSubscriptionData(workspaceId);

    // At the moment the internal sales portal does not support a customer being sold
    // more than one product, so we are getting just one product.
    const signUpProduct = storedSubscriptionData?.products?.[0];
    if (!signUpProduct) {
      return Promise.reject(new Error('No product found for the subscription'));
    }

    const { workspace } = this.props;

    if (workspace && workspace.subscription_state && signUpProduct?.name?.length === 0) {
      this.setState({ isEmptyProductSlug: true });
    }

    // Get estimates to know the price the user will have to pay
    const productEstimate = await fetchEstimateWithCoupons(
      workspaceId,
      signUpProduct.name ? (signUpProduct.name.toLowerCase() as ProductSlug) : PREDICT_PRODUCT_SLUG,
      signUpProduct.level ? signUpProduct.level : INTEGRATION_LEVEL,
      {
        term: storedSubscriptionData.term,
        coupon_ids: storedSubscriptionData.coupon_ids,
        quantity: signUpProduct.quantity,
        existing_customer: false,
        source: 'sales-portal',
        plan_type: storedSubscriptionData.plan_type,
        currency_code: storedSubscriptionData.currency_code
      }
    );

    return getPaymentInfoFromStoredDBData(storedSubscriptionData, productEstimate);
  };

  private setRedirectToPaymentPage = async (redirect: boolean) => {
    await updateUserProperty({
      property: UserProperties.redirect_to_payment_details,
      value: String(redirect)
    });
  };

  private resetSkipIntegrationsOnboardingStep = async () => {
    await updateUserProperty({
      property: UserProperties.skip_redirect_to_integrations_onboarding,
      value: 'false'
    });
  };

  private handleOnSuccessClick = async () => {
    const { navigate, params, onManageError } = this.props;
    const { organisationId } = this.state;
    const workspaceId = getParamFromRoute(params, ROUTE_PARAMS.WORKSPACE_ID) as string;

    try {
      const properties = await getUserProperties();
      const skipOnboardingIntegrationsStep =
        findProperty(properties, UserProperties.skip_redirect_to_integrations_onboarding)?.value === 'true';

      if (!workspaceId || !organisationId || skipOnboardingIntegrationsStep) {
        await this.resetSkipIntegrationsOnboardingStep();
        window.location.href = `${window.location.origin}${ROUTES.ROOT_ROUTE}`;
        return;
      }

      const integrationsOnboardingUrl = getIntegrationsOnboardingUrl(
        workspaceId,
        organisationId,
        PREDICT_PRODUCT_ID
      );

      navigate(integrationsOnboardingUrl);
    } catch (error) {
      onManageError(error);
    }
  };
}

const mapStateToProps = (state: StoreState, ownProps: RouterProps): StateToProps => {
  const workspaceId = getParamFromRoute(ownProps.params, ROUTE_PARAMS.WORKSPACE_ID) as string;
  const { normalised } = state;
  return {
    workspace: workspaceByIdSelector(normalised, workspaceId)
  };
};

export const PaymentDetailsContainerWE = withErrorBoundary(PaymentDetailsContainer);
export const PaymentDetailsContainerIntl = injectIntl(PaymentDetailsContainerWE);
export const PaymentDetailsContainerRedux = connect(mapStateToProps)(PaymentDetailsContainerIntl);

export const PaymentDetailsContainerWR: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  return <PaymentDetailsContainerRedux location={location} params={params} navigate={navigate} />;
};
